import { Button } from "./Button";
import { Clipboard } from "./Clipboard";
import { cn } from "../lib/cn";

/*
 * Danger is expressed in ink, not paper: the destructive confirm is a rubber
 * stamp outline, never a red fill. The under-inked mask comes from Stamp, but
 * not its tilt: this is the label on the most consequential control in the
 * product, and it is read before it is pressed. The click fires onConfirm
 * immediately - the stamped-down permanence belongs to the view that shows the
 * result, not to the button.
 *
 * The mask lives on this inner face rather than on the <button> so the
 * focus-visible outline paints at full strength outside it.
 */
const stampFace = cn(
  "inline-flex items-center justify-center gap-2 rounded-[2px] border-2 bg-transparent px-3.5 py-1.5",
  "font-mono text-[0.6875rem] font-medium uppercase tracking-[0.13em]",
  "[mask-image:radial-gradient(ellipse_at_center,black_60%,rgba(0,0,0,0.72)_100%)]",
  "transition duration-150 ease-paper motion-reduce:transition-none",
);

export interface ConfirmDialogProps extends Omit<
  React.DialogHTMLAttributes<HTMLDialogElement>,
  "open" | "title" | "children" | "onClose" | "onCancel"
> {
  open: boolean;
  /** Escape, backdrop, close button and the cancel action all land here. */
  onCancel: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  /** The explanation. Keep it to what the clinic needs to decide. */
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Turns the confirm action into a stopped-ink rubber stamp. Use it when the change is hard to undo. */
  destructive?: boolean;
  /** In-flight confirm: both buttons go inert and the clipboard stops dismissing. */
  busy?: boolean;
  /** Test hooks for the two actions: the buttons are built here, so a consumer's
   * suite has no other handle on them. */
  confirmTestId?: string;
  cancelTestId?: string;
  ref?: React.Ref<HTMLDialogElement>;
}

/**
 * The "are you sure" paper slip: a small Clipboard with an explanation and two
 * choices. Cancel is always the quiet one. A destructive confirm is a rubber
 * stamp you press, not a brighter button.
 *
 * Native props and `className` land on the `<dialog>`, the same as `Clipboard`'s
 * - so a consumer's own `data-testid` goes here rather than on a wrapper inside
 * the title. Note the dialog stays mounted while closed, so a handle that has to
 * mean "this confirm is up" is still the caller's to make conditional.
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  confirmTestId,
  cancelTestId,
  ...rest
}: ConfirmDialogProps) {
  return (
    <Clipboard
      {...rest}
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      /* A confirm already in flight has nothing to go back to. */
      dismissable={!busy}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid={cancelTestId}
          >
            {cancelLabel}
          </Button>
          {destructive ? (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              aria-busy={busy || undefined}
              data-testid={confirmTestId}
              className={cn(
                "group inline-flex select-none items-center justify-center rounded-[2px] bg-transparent",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                "disabled:cursor-not-allowed",
              )}
            >
              <span
                className={cn(
                  stampFace,
                  busy
                    ? "border-faint text-faint"
                    : "border-ink-stopped text-ink-stopped",
                )}
              >
                {busy && (
                  <>
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-3.5 shrink-0 animate-spin rounded-full",
                        "border-[1.5px] border-current border-t-transparent",
                        "motion-reduce:hidden",
                      )}
                    />
                    {/* Under reduced motion the spinner is replaced by a still ellipsis. */}
                    <span
                      aria-hidden="true"
                      className="hidden font-mono leading-none motion-reduce:inline"
                    >
                      ...
                    </span>
                  </>
                )}
                {confirmLabel}
              </span>
            </button>
          ) : (
            <Button
              variant="primary"
              onClick={onConfirm}
              busy={busy}
              data-testid={confirmTestId}
            >
              {confirmLabel}
            </Button>
          )}
        </>
      }
    >
      {children}
    </Clipboard>
  );
}
